import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { StandWorksheetService } from './stand-worksheet.service';
import { get, includes, isEqual, isFunction } from 'lodash';
import {
  STAND_WORKSHEET_STATUS_ENUM_OPTIONS_Lower,
  STAND_WORKSHEET_STATUS_VALUE,
} from '@app/shared/types/enums/stand-worksheet-status.enum';
import { UiActions } from '@app/store/actions/ui';
import { IBaseEntityType } from '@app/shared/types';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

@Injectable()
export class StandWorksheetStatusChangeService {
  @lazyInject(StandWorksheetService)
  public standWorksheetService: StandWorksheetService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;

  public constructor(public injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public async isSWStatusChangeAvailable(
    id: string,
    currentStatus: string,
  ): Promise<boolean | undefined> {
    let actualDocument: IBaseEntityType;
    try {
      actualDocument = await this.standWorksheetService
        .getDocumentById(id)
        .toPromise();
    } catch (e) {
      this.uiActions.showErrorToasterMessage({
        title: this.i18nService.instant(
          I18n.ErrorMessages.APIRequestTimeoutTitle,
        ),
        message: this.i18nService.instant(
          I18n.ErrorMessages.XVIAPIRequestError,
        ),
      });

      return;
    }

    const actualStatus = get(actualDocument, 'status');

    const unAvailableStatusMapping = {
      [STAND_WORKSHEET_STATUS_VALUE.Open]: [
        STAND_WORKSHEET_STATUS_VALUE.Approved,
        STAND_WORKSHEET_STATUS_VALUE.Canceled,
        STAND_WORKSHEET_STATUS_VALUE.Completed,
      ],
      [STAND_WORKSHEET_STATUS_VALUE.Approved]: [
        STAND_WORKSHEET_STATUS_VALUE.Open,
        STAND_WORKSHEET_STATUS_VALUE.Completed,
      ],
      [STAND_WORKSHEET_STATUS_VALUE.Canceled]: [
        STAND_WORKSHEET_STATUS_VALUE.Open,
        STAND_WORKSHEET_STATUS_VALUE.Completed,
      ],
    };

    const isChangeAvailable = !includes(
      unAvailableStatusMapping[currentStatus],
      actualStatus,
    );

    if (isChangeAvailable) {
      return true;
    }

    // do approve / save action
    if (isEqual(STAND_WORKSHEET_STATUS_VALUE.Open, currentStatus)) {
      this.uiActions.showToasterMessage({
        type: 'error',
        body: this.i18nService.instant(
          I18n.InfoMessages.CannotEditStandWorksheet,
          {
            actualStatus: this.i18nService.instant(
              STAND_WORKSHEET_STATUS_ENUM_OPTIONS_Lower[actualStatus],
            ),
          },
        ),
      });
    } else {
      // do reopen action
      this.uiActions.showToasterMessage({
        type: 'error',
        body: this.i18nService.instant(
          I18n.InfoMessages.CannotOperateStandWorksheet,
          {
            currentStatus: this.i18nService.instant(
              STAND_WORKSHEET_STATUS_ENUM_OPTIONS_Lower[currentStatus],
            ),
            actualStatus: this.i18nService.instant(I18n.Status.reopened),
          },
        ),
      });
    }

    return false;
  }
}
